import { supabase } from '@/integrations/supabase/client';

interface AuthRetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
}

interface AuthResult {
  error: any;
  data?: any;
}

/**
 * Detects if an error is related to network/proxy issues (like "Pong" responses)
 */
const isNetworkProxyError = (error: any): boolean => {
  if (!error) return false;
  
  const errorString = error.toString().toLowerCase();
  const errorMessage = error.message?.toLowerCase() || '';
  
  // Check for "Pong" response (the specific issue we're addressing)
  if (errorString.includes('pong') || errorMessage.includes('pong')) {
    return true;
  }
  
  // Check for JSON parsing errors that might indicate proxy responses
  if (error.name === 'SyntaxError' && errorMessage.includes('json')) {
    return true;
  }
  
  // Check for other network-related errors
  if (errorString.includes('fetch') || errorMessage.includes('fetch')) {
    return true;
  }
  
  return false;
};

/**
 * Creates a delay for retry logic with exponential backoff
 */
const createDelay = (attempt: number, baseDelay: number, maxDelay: number): Promise<void> => {
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  return new Promise(resolve => setTimeout(resolve, delay));
};

/**
 * Wraps authentication operations with retry logic for network/proxy errors
 */
export const withAuthRetry = async <T>(
  operation: () => Promise<T>,
  options: AuthRetryOptions = {}
): Promise<T> => {
  const { maxRetries = 3, baseDelay = 1000, maxDelay = 5000 } = options;
  
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // If this is not a network/proxy error, don't retry
      if (!isNetworkProxyError(error)) {
        throw error;
      }
      
      // If we've exhausted all retries, throw the last error
      if (attempt === maxRetries) {
        throw new Error(
          `Authentication failed after ${maxRetries + 1} attempts. This might be due to a network issue. Please try again in a moment.`
        );
      }
      
      // Wait before retrying
      await createDelay(attempt, baseDelay, maxDelay);
      
      console.log(`Authentication attempt ${attempt + 1} failed, retrying...`, error);
    }
  }
  
  throw lastError;
};

/**
 * Enhanced error messaging for authentication failures
 */
export const enhanceAuthError = (error: any): any => {
  if (!error) return null;
  
  if (isNetworkProxyError(error)) {
    return {
      ...error,
      message: 'Connection issue detected. Please check your internet connection and try again.',
      isNetworkError: true,
      originalError: error
    };
  }
  
  return error;
};

/**
 * Wrapper for sign in with email
 */
export const signInWithEmailWrapper = async (email: string, password: string): Promise<AuthResult> => {
  try {
    const result = await withAuthRetry(async () => {
      return await supabase.auth.signInWithPassword({ email, password });
    });
    
    return { error: enhanceAuthError(result.error), data: result.data };
  } catch (error) {
    return { error: enhanceAuthError(error) };
  }
};

/**
 * Wrapper for sign up with email
 */
export const signUpWithEmailWrapper = async (
  email: string, 
  password: string, 
  metadata?: { full_name: string; date_of_birth: string }
): Promise<AuthResult> => {
  try {
    const redirectUrl = `${window.location.origin}/`;
    
    const result = await withAuthRetry(async () => {
      return await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: metadata
        }
      });
    });
    
    return { error: enhanceAuthError(result.error), data: result.data };
  } catch (error) {
    return { error: enhanceAuthError(error) };
  }
};

/**
 * Wrapper for sign in with Google
 */
export const signInWithGoogleWrapper = async (): Promise<AuthResult> => {
  try {
    const result = await withAuthRetry(async () => {
      return await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });
    });
    
    return { error: enhanceAuthError(result.error), data: result.data };
  } catch (error) {
    return { error: enhanceAuthError(error) };
  }
};